import React from 'react';
import { TemplateRenderProps } from './types';
import { FormFields, AddonsSelector, CouponSection, TrustAndSecurityBadge, SocialProofAndFomo } from './CheckoutCommonElements';
import { BillingCycleSelector, PlanFeatureList, PriceBreakdown } from './CheckoutSummaryElements';

export const MonoTemplate: React.FC<TemplateRenderProps> = ({
  plan,
  config,
  formData,
  onInputChange,
  billingCycle,
  onChangeCycle,
  selectedAddonIds,
  onToggleAddon,
  appliedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
  onSubmitInfo,
  isCapturing,
  currencySymbol,
  addonsTotal,
  discountAmount,
  finalTotal,
}) => {
  return (
    <div
      className="min-h-full p-4 md:p-8 flex items-center justify-center bg-white text-black font-mono"
      style={{
        fontFamily: config.fontFamily || 'monospace',
      }}
    >
      <div className="w-full max-w-4xl border-4 border-black shadow-[10px_10px_0_0_#000] grid grid-cols-1 md:grid-cols-12 overflow-hidden">
        {/* Left Brutalist Column */}
        <div className="md:col-span-5 bg-black text-white p-6 md:p-8 flex flex-col justify-between space-y-6">
          <div className="space-y-4">
            {config.logoUrl && <img src={config.logoUrl} alt="Logo" className="h-6 filter invert object-contain" />}
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">ITEM::TIER</p>
              <h2 className="text-3xl font-black uppercase italic tracking-tighter">{plan.tier}</h2>
              {plan.description && <p className="text-xs text-zinc-300 mt-1">{plan.description}</p>}
            </div>

            <BillingCycleSelector billingCycle={billingCycle} onChangeCycle={onChangeCycle} config={config} />
            <PlanFeatureList features={plan.features} textColorClass="text-zinc-200" checkColorClass="text-yellow-400" />
          </div>

          <div className="border-2 border-white p-4 bg-black">
            <p className="text-[10px] uppercase font-bold text-zinc-400">GRAND TOTAL</p>
            <p className="text-3xl font-black">{currencySymbol}{finalTotal.toFixed(2)}</p>
          </div>
        </div>

        {/* Right Form Column */}
        <div className="md:col-span-7 p-6 md:p-8 space-y-5 bg-white">
          <div className="border-b-4 border-black pb-2">
            <h1 className="text-xl font-black uppercase">{config.headerMain || 'EXECUTE TRANSACTION'}</h1>
            <p className="text-xs text-zinc-600">{config.headerSub || 'Fill required parameters to initiate.'}</p>
          </div>

          <SocialProofAndFomo config={config} />

          <form onSubmit={onSubmitInfo} className="space-y-4">
            <FormFields
              config={config}
              formData={formData}
              onInputChange={onInputChange}
              inputClassName="border-2 border-black rounded-none font-mono text-xs focus:bg-yellow-50"
              labelClassName="font-mono text-xs font-bold uppercase"
              customStyle={{ borderRadius: '0px' }}
            />
            <AddonsSelector addons={config.addons} selectedAddonIds={selectedAddonIds} onToggleAddon={onToggleAddon} currency={plan.currency} />
            <CouponSection config={config} appliedCoupon={appliedCoupon} onApplyCoupon={onApplyCoupon} onRemoveCoupon={onRemoveCoupon} />

            <button
              type="submit"
              disabled={isCapturing}
              className="w-full py-4 bg-black text-white font-black text-sm uppercase tracking-tighter border-2 border-black hover:bg-zinc-800 transition-all rounded-none disabled:opacity-50"
            >
              {isCapturing ? 'COMPILING...' : config.buttonText || '>> AUTHORIZE PAYMENT'}
            </button>
          </form>

          <TrustAndSecurityBadge config={config} />
        </div>
      </div>
    </div>
  );
};
